import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

export const SHORT_BREAK_REASONS = [
  'Lunch',
  'Food',
  'Dinner',
  'Need to go out',
  'Urgency',
  'Add your reason',
];

const CUSTOM_REASON = 'Add your reason';

const formatElapsed = (totalSeconds) => {
  const secs = Math.max(0, Number(totalSeconds) || 0);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const ShortBreakModal = ({
  status,
  onClose,
  onStartBreak,
  onJoinBack,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(status?.elapsedSeconds || 0);

  const onBreak = Boolean(status?.onBreak);

  useEffect(() => {
    if (!onBreak) return undefined;
    setElapsedSeconds(status?.elapsedSeconds || 0);
    const startedMs = status?.breakStartedAt
      ? Date.parse(status.breakStartedAt)
      : Date.now() - ((status?.elapsedSeconds || 0) * 1000);
    const tick = () => {
      const base = Number.isFinite(startedMs) ? startedMs : Date.now();
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - base) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [onBreak, status?.breakStartedAt, status?.elapsedSeconds]);

  const reasonValue = useMemo(() => {
    if (selectedReason === CUSTOM_REASON) {
      return customReason.trim();
    }
    return selectedReason.trim();
  }, [selectedReason, customReason]);

  const handleStart = async () => {
    if (submitting) return;
    if (!reasonValue) {
      setError(selectedReason === CUSTOM_REASON
        ? 'Please enter your reason'
        : 'Please select a reason for your short break');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onStartBreak(reasonValue);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to start short break');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinBack = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onJoinBack();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to join back');
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl border border-sky-200/70 dark:border-sky-800 bg-white dark:bg-gray-800"
        role="dialog"
        aria-labelledby="short-break-title"
      >
        <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-sky-500 via-cyan-600 to-teal-600 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p className="text-sky-50/90 text-xs sm:text-sm font-medium">
            {onBreak ? 'Currently on short break' : 'Take a short break'}
          </p>
          <h2 id="short-break-title" className="text-2xl font-bold tracking-tight mt-1 pr-8">
            {onBreak ? 'Join Back' : 'Short Break'}
          </h2>
          {onBreak && (
            <div className="mt-4 rounded-xl bg-white/15 border border-white/25 px-4 py-3 text-center">
              <p className="text-4xl font-bold tabular-nums tracking-tight">{formatElapsed(elapsedSeconds)}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/85 mt-1">
                Break timer
                {status?.reason ? ` · ${status.reason}` : ''}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {onBreak ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              When you are ready, join back to end this break. Time spent will be included in your day report.
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <label
                  htmlFor="short-break-reason"
                  className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                >
                  Reason of Short Break
                </label>
                <select
                  id="short-break-reason"
                  value={selectedReason}
                  onChange={(e) => {
                    setSelectedReason(e.target.value);
                    setError('');
                  }}
                  className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                >
                  <option value="">Select reason…</option>
                  {SHORT_BREAK_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {selectedReason === CUSTOM_REASON && (
                <div className="space-y-1.5">
                  <label
                    htmlFor="short-break-custom-reason"
                    className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Your reason
                  </label>
                  <input
                    id="short-break-custom-reason"
                    type="text"
                    value={customReason}
                    onChange={(e) => {
                      setCustomReason(e.target.value);
                      setError('');
                    }}
                    maxLength={120}
                    placeholder="Type your reason…"
                    className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                  />
                </div>
              )}

              {(status?.totalBreakMinutesToday > 0) && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Breaks taken today: {status.totalBreakTimeToday || `${status.totalBreakMinutesToday}m`}
                  {status.breakCountToday > 0 ? ` · ${status.breakCountToday} so far` : ''}
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-5 sm:px-6 pb-5 flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
          >
            Cancel
          </button>
          {onBreak ? (
            <button
              type="button"
              onClick={handleJoinBack}
              disabled={submitting}
              className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-3 transition-colors disabled:opacity-70 shadow-sm"
            >
              {submitting ? 'Joining back…' : 'Join Back'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleStart}
              disabled={submitting || !selectedReason}
              className="flex-1 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold py-3 transition-colors disabled:opacity-70 shadow-sm"
            >
              {submitting ? 'Starting…' : 'Start Short Break'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ShortBreakModal;
